import React, { type FC, useState } from 'react';

import classNames from 'classnames';

import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import {
  getLatestActiveVersionId,
  selectAssetVersionsExtended,
} from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';

import type { SelectedVersions } from '../../components/modals/share/types';
import { useAppSelector } from '../../store/react_redux_typed';
import { filterLatestActiveVersions } from '../../utils/filter_latest_active_versions';
import VersionSelectorAssetItem from './version_selector_asset_item';
import VersionSelectorVersionItem from './version_selector_version_item';

import styles from './version_selector.module.css';

type VersionSelectorProps = {
  selectedItems: SearchItemEntity[];
  className?: string;
  selectedVersions: SelectedVersions;
  onSetSelectedVersions: (value: SelectedVersions) => void;
};

type AssetVersionProps = {
  selectedItem: SearchItemEntity;
  selectedVersions: SelectedVersions;
  onSetSelectedVersions: (value: SelectedVersions) => void;
};

function AssetVersion({
  selectedItem,
  onSetSelectedVersions,
  selectedVersions,
}: AssetVersionProps) {
  const latestVersionId = getLatestActiveVersionId(selectedItem.versions);
  const assetVersions = useAppSelector((state) =>
    selectAssetVersionsExtended(state, selectedItem.id),
  );
  const activeVersions = filterLatestActiveVersions(assetVersions);

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={styles.asset}>
      <VersionSelectorAssetItem
        latestVersionId={latestVersionId}
        selectedItem={selectedItem}
        isExpandable={activeVersions.length > 1}
        onExpand={() => setIsExpanded((prev) => !prev)}
        isExpanded={isExpanded}
        onSelectAllVersions={(isSelected) => {
          onSetSelectedVersions({
            ...selectedVersions,
            [selectedItem.id]: isSelected
              ? activeVersions.map((item) => item.id)
              : [],
          });
        }}
        selectedItemsCount={selectedVersions[selectedItem.id]?.length ?? 0}
        itemsCount={activeVersions.length}
      />
      {activeVersions.length > 1 ? (
        <div
          className={classNames({
            [styles.hidden]: !isExpanded,
          })}
        >
          {activeVersions.map((versionItem) => (
            <VersionSelectorVersionItem
              key={versionItem.id}
              latestVersionId={latestVersionId}
              versionItem={versionItem}
              assetId={selectedItem.id}
              onSetSelectedVersion={(isSelected) => {
                const newValue = isSelected
                  ? [
                      ...(selectedVersions[selectedItem.id] ?? []),
                      versionItem.id,
                    ]
                  : (selectedVersions[selectedItem.id] ?? []).filter(
                      (id) => id !== versionItem.id,
                    );

                onSetSelectedVersions({
                  ...selectedVersions,
                  [selectedItem.id]: newValue,
                });
              }}
              isSelected={selectedVersions[selectedItem.id]?.includes(
                versionItem.id,
              )}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

const VersionSelector: FC<VersionSelectorProps> = ({
  selectedItems,
  className,
  selectedVersions,
  onSetSelectedVersions,
}) => {
  return (
    <div className={classNames(styles.root, className)}>
      {selectedItems.map((item) => {
        const noVersionsSelected = !selectedVersions[item.id]?.length;
        return (
          <React.Fragment key={item.id}>
            <AssetVersion
              key={item.id}
              onSetSelectedVersions={onSetSelectedVersions}
              selectedVersions={selectedVersions}
              selectedItem={item}
            />
            {noVersionsSelected ? (
              <span className={styles.error_message}>
                At least one version must be selected
              </span>
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default VersionSelector;
